import React, { useContext, useMemo, useState } from "react";
import {
  Button,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "@/design-system/native";
import { CheckCircle, ChevronDown, Columns2, Edit, Wrench, X, XCircle, Filter, Search as SearchIcon, Menu, Smartphone } from "lucide-react";

import styles from "./TicketListTab.module.scss";
import TableCard from "../../Tables/DataTable/TableCard";
import DataTable from "../../Tables/DataTable/DataTable";
import Pager from "../../Tables/DataTable/Pagination";
import ImportButton from "../../Common/ImportButton";
import ExportButtons from "../../Common/ExportButtons";
import { useNavigate } from "react-router";
import CustomizerContext from "../../../_helper/Customizer";


const TicketListTab = ({
  tickets,
  setTickets,
  onEdit,
  onDelete,
  batchUpdate = false,
  onToggleBatch,
  selectedIds = new Set(),
  onToggleRow,
  onClearSelection,
}) => {
  // toolbar state
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { layoutURL } = useContext(CustomizerContext);

  // tek filters objesi
  const [filters, setFilters] = useState({
    status: "",
    client: "",
    project: "",
    type: "",
    label: "",
    assigned: "",
    created: "",
  });

  // columns visibility
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(() => new Set());
  const toggleCol = (id) =>
    setHiddenCols((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // sort + paging
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // status quick buttons
  const filterByStatusBtn = (status) =>
    setFilters((f) => ({
      ...f,
      status: f.status === status ? "" : status,
    }));

  // filtering
  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        `${t.id} ${t.title} ${t.client} ${t.project} ${t.type} ${t.assigned} ${t.activity}`
          .toLowerCase()
          .includes(q);

      const sOk = !filters.status || t.status === filters.status;
      const cOk = !filters.client || t.client === filters.client;
      const pOk = !filters.project || t.project === filters.project;
      const tOk = !filters.type || t.type === filters.type;
      const lOk = !filters.label || (t.label || "-") === filters.label;
      const aOk = !filters.assigned || t.assigned === filters.assigned;
      const crOk =
        !filters.created || String(t.activity).includes(filters.created);

      return matchSearch && sOk && cOk && pOk && tOk && lOk && aOk && crOk;
    });
  }, [tickets, search, filters]);

  // columns
  const columns = useMemo(() => {
    const common = [
      {
        id: "ticketId",
        header: "Ticket ID",
        accessor: "id",
        cell: (row) => (
          <span
            className={styles.idLink}
            onClick={() =>
              navigate(
                `/tickets/tickets-detail/${row.id}/${layoutURL}`
              )
            }
          >
            Ticket #{row.id}
          </span>
        ),
      },
      { id: "title", header: "Title", accessor: "title" },
      { id: "client", header: "Client", accessor: "client" },
      { id: "project", header: "Project", accessor: "project" },
      { id: "type", header: "Type", accessor: "type" },
      { id: "assigned", header: "Assigned", accessor: "assigned" },
      { id: "activity", header: "Activity", accessor: "activity" },
      { id: "status", header: "Status", accessor: "status" },
      {
        id: "actions",
        header: <Menu />,
        cell: (row) => (
          <div className={styles.rowActions}>
            <Button
              onClick={() =>
                navigate(
                  `/tickets/tickets-detail/${row.id}/${layoutURL}`
                )
              }
              size="xs"
              color="light"
              className={styles.actionBtn}
            >
              <Smartphone size={16} color="#10161b" />
            </Button>

            <UncontrolledDropdown direction="down" caret>
              <DropdownToggle
                size="sm"
                color="white"
                className={styles.menuToggle}
              >
                <Wrench color="#10161b" size={18} />
                <ChevronDown color="#10161b" size={12} />
              </DropdownToggle>

              <DropdownMenu
                end
                container="body"
                modifiers={[
                  {
                    name: "preventOverflow",
                    options: { boundary: "viewport" },
                  },
                ]}
                className={styles.menu}
              >
                <DropdownItem
                  onClick={() => onEdit?.(row)}
                  className={styles.menuItem}
                >
                  <Edit color="#10161b" size={16} /> Edit
                </DropdownItem>
                <DropdownItem
                  onClick={() => handleToggleStatus(row.id)}
                  className={styles.menuItem}
                >
                  <CheckCircle size={14} className={styles.menuItemIcon} />
                  {row.status === "Closed" ? "Mark as Open" : "Mark as Closed"}
                </DropdownItem>
                <DropdownItem
                  className={styles.menuItem}
                  onClick={() => onDelete?.(row.id)}
                >
                  <X color="#10161b" size={16} /> Delete
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        ),
      },
    ];

    if (batchUpdate) {
      return [
        {
          id: "select",
          header: "-",
          width: 44,
          cell: (row) => (
            <Input
              type="checkbox"
              className={styles.rowCheckbox}
              checked={selectedIds?.has(row.id)}
              onChange={() => onToggleRow?.(row.id)}
            />
          ),
        },
        ...common,
      ];
    }
    return common;
  }, [batchUpdate, onEdit, onDelete, onToggleRow, selectedIds]);

  // sorting
  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const col = columns.find((c) => c.id === sortBy);
    if (!col) return filtered;

    const getVal = (r) =>
      typeof col.accessor === "function" ? col.accessor(r) : r[col.accessor];

    const arr = [...filtered].sort((a, b) => {
      const A = (getVal(a) ?? "").toString().toLowerCase();
      const B = (getVal(b) ?? "").toString().toLowerCase();
      if (A < B) return -1;
      if (A > B) return 1;
      return 0;
    });
    return sortDir === "asc" ? arr : arr.reverse();
  }, [filtered, sortBy, sortDir, columns]);

  // paging
  const total = sorted.length;
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  // header slots
  const leftSlot = (
    <div className={styles.toolbarSide}>
      {/* Columns visibility */}
      <Dropdown isOpen={colMenuOpen} toggle={() => setColMenuOpen((v) => !v)}>
        <DropdownToggle
          color="white"
          className={`${styles.toolbarBtn} ${styles.colsToggle}`}
        >
          <Columns2 size={16} />
        </DropdownToggle>
        <DropdownMenu className={styles.colsMenu}>
          {columns.map((col) => (
            <DropdownItem
              key={col.id}
              toggle={false}
              onClick={() => toggleCol(col.id)}
            >
              <input
                type="checkbox"
                readOnly
                checked={!hiddenCols.has(col.id)}
                className={styles.colCheckbox}
              />
              {col.header || "(untitled)"}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>

      <Button
        color="white"
        className={styles.toolbarBtn}
        onClick={() => setShowFilters((v) => !v)}
      >
        <Filter size={16} /> + Add new filter
      </Button>
    </div>
  );

  const rightSlot = (
    <div className={styles.toolbarSide}>
      <div className={styles.exportGroup}>
        <ImportButton
          title="Import tickets"
          label="Import tickets"
          columns={columns}
          setRows={setTickets}
          className={styles.toolbarBtn}
        />
        <ExportButtons
          rows={sorted}
          columns={columns}
          fileName="tickets"
          sheetName="Tickets"
          title="Tickets"
        />
      </div>
      <div className={styles.searchWrap}>
        <Input
          type="search"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className={styles.searchInput}
        />
        <SearchIcon size={16} className={styles.searchIcon} />
      </div>
    </div>
  );

  const handleToggleStatus = (id) => {
    setTickets((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const nextStatus = t.status === "Closed" ? "Open" : "Closed";
        return { ...t, status: nextStatus };
      })
    );
  };

  // filter configs
  const filterConfigs = [
    { key: "client", label: "- Client -" },
    { key: "project", label: "- Project -" },
    { key: "type", label: "- Ticket type -" },
    { key: "label", label: "- Label -" },
    { key: "assigned", label: "- Assigned to -" },
    { key: "created", label: "- Created -" },
  ];

  return (
    <TableCard title="Ticket list" leftSlot={leftSlot} rightSlot={rightSlot}>
      {showFilters && (
        <div className={styles.filterRow}>
          {filterConfigs.map((cfg) => {
            const options = [
              ...new Set(
                tickets.map((t) => {
                  if (cfg.key === "label") return t.label || "-";
                  if (cfg.key === "created") return t.activity;
                  return t[cfg.key];
                })
              ),
            ].filter(Boolean);

            return (
              <div className={styles.filterCol} key={cfg.key}>
                <Input
                  bsSize="sm"
                  type="select"
                  className={styles.filterSelect}
                  value={filters[cfg.key]}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, [cfg.key]: e.target.value }));
                    setPage(1);
                  }}
                >
                  <option value="">{cfg.label}</option>
                  {options.map((opt, i) => (
                    <option key={i} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Input>
              </div>
            );
          })}

          <div className={styles.filterActions}>
            <div className={styles.segment}>
              <Button
                color="white"
                className={`${styles.segmentBtn} ${styles.segmentBtnFirst} ${
                  filters.status === "Open" ? styles.segmentBtnActive : ""
                }`}
                onClick={() => filterByStatusBtn("Open")}
              >
                Open
              </Button>
              <Button
                color="white"
                className={`${styles.segmentBtn} ${styles.segmentBtnLast} ${
                  filters.status === "Closed" ? styles.segmentBtnActive : ""
                }`}
                onClick={() => filterByStatusBtn("Closed")}
              >
                Closed
              </Button>
            </div>

            <Button
              color="white"
              className={styles.resetBtn}
              size="sm"
              onClick={() => {
                setFilters({
                  status: "",
                  client: "",
                  project: "",
                  type: "",
                  label: "",
                  assigned: "",
                  created: "",
                });
                setSearch("");
                setPage(1);
              }}
              title="Reset"
            >
              <CheckCircle color="white" size={18} />
            </Button>
            <Button
              color="white"
              className={styles.closeFiltersBtn}
              size="sm"
              onClick={() => setShowFilters(false)}
            >
              <XCircle color="#10161b" size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* DataTable */}
      <div className={styles.tableRegion}>
        <DataTable
          columns={columns}
          rows={paged}
          hiddenColumnIds={[...hiddenCols]}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={(id, dir) => {
            setSortBy(id);
            setSortDir(dir);
          }}
        />
      </div>

      <Pager
        page={page}
        pageSize={pageSize}
        total={total}
        onChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        pageSizes={[10, 25, 50, 100]}
      />
    </TableCard>
  );
};

export default TicketListTab;
